import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenants } from './consultas';

/**
 * Listado de tenants, de solo lectura. Ruta `/tenants`, sin `RequiereRol`: usa
 * `GET /api/v1/consultas/tenants`, la misma superficie que ya ve un `OPERADOR`
 * en el selector de emisión y en la lista blanca de un sistema — acá no se
 * expone nada nuevo, solo se hace visible lo que ya se podía consultar.
 *
 * # PARA QUÉ EXISTE
 *
 * Antes de esto, el único id que se veía en pantalla era el que el propio
 * selector de `/emision` o de la lista blanca ya resolvía por nombre. Para
 * cualquier otro uso —autorizar un tenant en un sistema por API, armar una
 * llamada de Postman, confirmar a qué tenant corresponde una razón social—
 * había que ir a buscar el id a la base o a un log. Esta pantalla es esa
 * consulta, hecha una sola vez: **id, razón social, CUIT, punto de venta y si
 * hoy puede emitir**, con un filtro de texto para no tener que leer la lista
 * entera.
 *
 * # POR QUÉ NO TIENE BOTONES
 *
 * El ABM de tenants (alta, `PATCH` de datos y punto de venta) vive en
 * `/api/v1/admin/tenants` y no tiene pantalla propia todavía: eso es una
 * tarjeta aparte, no esta. Acá se mira, no se modifica.
 */
export function PantallaDeTenants() {
  const idFiltro = useId();
  const [filtro, setFiltro] = useState('');
  const tenants = useTenants();

  const filtrados = (tenants.data ?? []).filter((t) =>
    `${t.razonSocial} ${t.cuit}`.toLowerCase().includes(filtro.trim().toLowerCase()),
  );

  return (
    <main>
      <p>
        <Link to="/">Inicio</Link>
      </p>
      <h1>Tenants</h1>
      <p className="nota">
        Cada fila es alguien a cuyo nombre se puede emitir. El <strong>id</strong> es el que hay que
        usar para autorizar un tenant en un sistema conectado (
        <Link to="/admin/sistemas">Administración → Sistemas conectados</Link>) o para armar un
        pedido a la API: nunca el CUIT.
      </p>

      {tenants.isPending ? <p role="status">Cargando tenants…</p> : null}
      {tenants.isError ? (
        <p role="alert" className="error">
          No se pudo traer la lista de tenants. Probá de nuevo.
        </p>
      ) : null}

      {tenants.data ? (
        <>
          <p>
            <label htmlFor={idFiltro}>Buscar por razón social o CUIT</label>
            <br />
            <input
              id={idFiltro}
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Ej: De Barba, o 20357540977"
            />
          </p>

          {tenants.data.length === 0 ? (
            <p className="nota">No hay ningún tenant dado de alta todavía.</p>
          ) : filtrados.length === 0 ? (
            <p className="nota">Ningún tenant coincide con &quot;{filtro}&quot;.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th scope="col">Id</th>
                  <th scope="col">Razón social</th>
                  <th scope="col">CUIT</th>
                  <th scope="col">Punto de venta</th>
                  <th scope="col">Puede emitir</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((t) => (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td>{t.razonSocial}</td>
                    <td>{t.cuit}</td>
                    <td>{t.puntoVenta}</td>
                    <td>
                      {t.puedeEmitir ? 'Sí' : 'No (inactivo o sin delegación de ARCA activa)'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}
    </main>
  );
}
